import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { toast } from 'sonner';
import { formatPhone } from '../utils/formatPhone';
import {
  Users, Search, Download, ChevronDown, ChevronRight, Mail, Phone,
  Award, Calendar, User, X, Send, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import PageLayout from '../components/PageLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Role color mapping (same as ParticipantsManagement)
const getRoleColor = (workRoles = []) => {
  const r = workRoles.map(s => s.toLowerCase()).join(' ');
  if (/surgeon|surgery|physician|doctor|md|gyn|gynecolog|urogyn|peds|pediatric|podiatr|gastro|gi\b|plastics|hand surg|urolog/.test(r)) return 'orange';
  if (/anesthesi|crna|anesthetist/.test(r)) return 'yellow';
  if (/pacu|recovery/.test(r)) return 'green';
  if (/nurse|tech|scrub|or\b|or tech|or nurse/.test(r)) return 'blue';
  return null;
};

const ROLE_COLORS = {
  orange: 'bg-orange-100 text-orange-700 border-orange-200',
  yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
};

// Directory entry row component
const DirectoryRow = ({ entry, expanded, onToggle, selected, onSelect, onDelete }) => {
  const roleColor = getRoleColor(entry.work_roles);
  const roleClass = roleColor ? ROLE_COLORS[roleColor] : 'bg-slate-100 text-slate-600 border-slate-200';
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    onSelect(entry.user_id);
  };

  return (
    <div className="border-b border-slate-100 last:border-0">
      {/* Main row */}
      <div
        className={`flex items-center gap-3 px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors ${selected ? 'bg-primary/5' : ''}`}
        onClick={onToggle}
        data-testid={`directory-row-${entry.user_id}`}
      >
        {/* Checkbox */}
        <div onClick={handleCheckboxClick} className="flex-shrink-0">
          <Checkbox
            checked={selected}
            className="data-[state=checked]:bg-primary"
            data-testid={`select-${entry.user_id}`}
          />
        </div>

        {/* Expand/collapse */}
        <button className="text-slate-400 hover:text-slate-600 flex-shrink-0">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        {/* Avatar placeholder */}
        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4 text-primary" />
        </div>

        {/* Name & role */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-slate-800 text-sm">{entry.name}</span>
            {entry.is_veteran && (
              <Badge className="bg-amber-100 text-amber-700 border-0 text-xs flex items-center gap-1">
                <Award className="w-3 h-3" />Veteran
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {entry.work_roles.length > 0 ? (
              entry.work_roles.slice(0, 2).map(role => (
                <Badge key={role} className={`text-xs border ${roleClass}`}>{role}</Badge>
              ))
            ) : (
              <span className="text-xs text-slate-400">No role on file</span>
            )}
            {entry.organization && (
              <span className="text-xs text-slate-400">· {entry.organization}</span>
            )}
          </div>
        </div>

        {/* Contact info */}
        <div className="hidden sm:flex items-center gap-4 text-sm text-slate-500">
          {entry.email && (
            <a
              href={`mailto:${entry.email}`}
              onClick={e => e.stopPropagation()}
              className="flex items-center gap-1 hover:text-primary transition-colors"
              title={entry.email}
            >
              <Mail className="w-4 h-4" />
              <span className="hidden md:inline max-w-[180px] truncate">{entry.email}</span>
            </a>
          )}
          {entry.phone && (
            <a
              href={`tel:${entry.phone}`}
              onClick={e => e.stopPropagation()}
              className="flex items-center gap-1 hover:text-primary transition-colors"
              title={entry.phone}
            >
              <Phone className="w-4 h-4" />
              <span className="hidden lg:inline">{formatPhone(entry.phone)}</span>
            </a>
          )}
        </div>

        {/* Trip count */}
        <div className="flex items-center gap-1 text-sm text-slate-500 flex-shrink-0">
          <Calendar className="w-4 h-4" />
          <span>{entry.trip_count} trip{entry.trip_count !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 pl-20 bg-slate-50/50">
          {/* Contact info (visible on mobile when expanded) */}
          <div className="sm:hidden flex flex-wrap gap-3 mb-3 pt-2">
            {entry.email && (
              <a href={`mailto:${entry.email}`} className="flex items-center gap-1 text-sm text-primary">
                <Mail className="w-4 h-4" />{entry.email}
              </a>
            )}
            {entry.phone && (
              <a href={`tel:${entry.phone}`} className="flex items-center gap-1 text-sm text-primary">
                <Phone className="w-4 h-4" />{formatPhone(entry.phone)}
              </a>
            )}
          </div>

          {/* Engagement summary */}
          <div className="flex flex-wrap gap-4 text-xs text-slate-500 mb-3">
            {entry.first_trip_year && (
              <span>First trip: <strong>{entry.first_trip_year}</strong></span>
            )}
            {entry.last_trip_year && (
              <span>Most recent: <strong>{entry.last_trip_year}</strong></span>
            )}
            {entry.trip_count > 0 && (
              <span>Total: <strong>{entry.trip_count} trip{entry.trip_count !== 1 ? 's' : ''}</strong></span>
            )}
          </div>

          {/* Trip history */}
          {entry.trip_history.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Trip History</p>
              <div className="space-y-1.5">
                {entry.trip_history.map((trip, idx) => (
                  <div key={trip.trip_id || idx} className="flex items-start gap-3 text-sm">
                    <span className="text-slate-400 w-10 flex-shrink-0">{trip.year || '—'}</span>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-slate-700">{trip.trip_name}</span>
                      {trip.responsibilities?.length > 0 && (
                        <span className="text-slate-500 ml-2">
                          — {trip.responsibilities.join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400 italic">No trip history</p>
          )}

          {/* Admin notes */}
          {entry.admin_notes && (
            <div className="mt-3 p-2 bg-amber-50 rounded-lg border border-amber-100">
              <p className="text-xs font-medium text-amber-700 mb-0.5">Admin Notes</p>
              <p className="text-sm text-amber-800">{entry.admin_notes}</p>
            </div>
          )}

          {/* Delete user */}
          <div className="mt-4 pt-3 border-t border-slate-100 flex justify-end">
            {!confirmDelete ? (
              <button
                onClick={(e) => { e.stopPropagation(); setConfirmDelete(true); }}
                className="text-xs text-slate-400 hover:text-red-500 transition-colors flex items-center gap-1"
                data-testid={`delete-user-${entry.user_id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
                Remove from system
              </button>
            ) : (
              <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                <span className="text-xs text-red-600 font-medium">Delete {entry.name} and all their data?</span>
                <button
                  onClick={() => { onDelete(entry.user_id, entry.name); setConfirmDelete(false); }}
                  className="text-xs bg-red-600 text-white px-3 py-1 rounded-full hover:bg-red-700 transition-colors"
                  data-testid={`confirm-delete-${entry.user_id}`}
                >
                  Yes, delete
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Bulk Email Modal
const BulkEmailModal = ({ open, onClose, selectedEmails, selectedNames }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [tripTag, setTripTag] = useState('');
  const [trips, setTrips] = useState([]);

  // Fetch templates and trips when modal opens
  useEffect(() => {
    if (open) {
      setLoadingTemplates(true);
      Promise.all([
        api.get('/site-settings'),
        api.get('/trips'),
      ]).then(([settingsRes, tripsRes]) => {
        const tpls = settingsRes.data?.directory_email_templates || [];
        setTemplates(tpls.filter(t => t.subject || t.body));
        setTrips(tripsRes.data || []);
      }).catch(() => {
        setTemplates([]);
        setTrips([]);
      }).finally(() => setLoadingTemplates(false));
    }
  }, [open]);

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject);
    setBody(tpl.body);
  };

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!body.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);
    try {
      const res = await api.post('/directory/send-bulk-email', {
        emails: selectedEmails,
        subject: subject.trim(),
        body: body.trim(),
        trip_tag: tripTag,
        template_name: '',
      });

      if (res.data.success) {
        toast.success(`Email sent to ${res.data.sent_count} recipient${res.data.sent_count !== 1 ? 's' : ''}`);
        if (res.data.failed?.length > 0) {
          toast.warning(`Failed to send to ${res.data.failed.length} recipient(s)`);
        }
        setSubject('');
        setBody('');
        setTripTag('');
        onClose();
      } else {
        toast.error(res.data.error || 'Failed to send email');
      }
    } catch (e) {
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Compose Email
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Templates */}
          {templates.length > 0 && (
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Use Template</label>
              <div className="flex flex-wrap gap-2">
                {templates.map((tpl, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs rounded-full"
                    onClick={() => applyTemplate(tpl)}
                    data-testid={`use-template-${idx}`}
                  >
                    {tpl.name || `Template ${idx + 1}`}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Recipients */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">To</label>
            <div className="p-2 bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-600 max-h-24 overflow-y-auto">
              {selectedNames.length <= 5 ? (
                selectedNames.join(', ')
              ) : (
                <span>{selectedNames.slice(0, 3).join(', ')} and {selectedNames.length - 3} others</span>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-1">{selectedEmails.length} recipient{selectedEmails.length !== 1 ? 's' : ''}</p>
          </div>

          {/* Trip Tag */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Related to Trip <span className="text-xs text-slate-400 font-normal">(optional — for tracking)</span></label>
            <Select value={tripTag || 'none'} onValueChange={(v) => setTripTag(v === 'none' ? '' : v)}>
              <SelectTrigger className="h-9" data-testid="email-trip-tag">
                <SelectValue placeholder="None — general email" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None — general email</SelectItem>
                {[...new Set(trips.map(t => {
                  const year = t.start_date?.substring(0, 4);
                  return year;
                }).filter(Boolean))].sort().reverse().map(year => (
                  <SelectItem key={`year-${year}`} value={`${year} Trips`}>{year} Trips (general)</SelectItem>
                ))}
                {trips.filter(t => t.status !== 'completed').map(t => (
                  <SelectItem key={t.trip_id} value={t.name}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Subject */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Subject</label>
            <Input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Enter email subject..."
              data-testid="email-subject"
            />
          </div>

          {/* Body */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Message</label>
            <Textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="Enter your message..."
              rows={8}
              data-testid="email-body"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button onClick={handleSend} disabled={sending} data-testid="send-email-btn">
            {sending ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Send Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Directory Page
const DirectoryPage = () => {
  const [directory, setDirectory] = useState([]);
  const [stats, setStats] = useState({ total_participants: 0, veterans: 0, years_active: [] });
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState(new Set());

  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [tripCountFilter, setTripCountFilter] = useState('all');
  const [contactedFilter, setContactedFilter] = useState('all'); // 'all' | 'contacted' | 'not_contacted'
  const [contactedTripTag, setContactedTripTag] = useState('');
  const [contactedEmails, setContactedEmails] = useState(new Set());
  const [trips, setTrips] = useState([]);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchDirectory = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (yearFilter !== 'all') params.append('trip_year', yearFilter);
      if (tripCountFilter === 'veteran') params.append('min_trips', '3');
      if (tripCountFilter === 'firsttimer') {
        params.append('min_trips', '1');
        params.append('max_trips', '1');
      }
      if (tripCountFilter === 'none') {
        params.append('max_trips', '0');
      }

      const [dirRes, statsRes] = await Promise.all([
        api.get(`/directory?${params.toString()}`),
        api.get('/directory/stats'),
      ]);
      setDirectory(dirRes.data);
      setStats(statsRes.data);
      // Clear selections when filters change
      setSelectedIds(new Set());
    } catch (e) {
      toast.error('Failed to load directory');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, roleFilter, yearFilter, tripCountFilter]);

  useEffect(() => {
    fetchDirectory();
  }, [fetchDirectory]);

  // Fetch trips for contact filter and fetch contacted emails when trip tag changes
  useEffect(() => {
    api.get('/trips').then(res => setTrips(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (contactedTripTag) {
      api.get(`/directory/email-log/contacted-emails?trip_tag=${encodeURIComponent(contactedTripTag)}`)
        .then(res => setContactedEmails(new Set(res.data.emails || [])))
        .catch(() => setContactedEmails(new Set()));
    } else {
      setContactedEmails(new Set());
    }
  }, [contactedTripTag]);

  const toggleExpanded = (userId) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const toggleSelected = (userId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleDeleteUser = async (userId, name) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(`${name} has been removed from the system`);
      fetchDirectory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete user');
    }
  };


  const selectAll = () => {
    if (selectedIds.size === directory.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(directory.map(e => e.user_id)));
    }
  };

  const handleExport = () => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.append('search', debouncedSearch);
    if (roleFilter !== 'all') params.append('role', roleFilter);
    if (yearFilter !== 'all') params.append('trip_year', yearFilter);
    if (tripCountFilter === 'veteran') params.append('min_trips', '3');
    if (tripCountFilter === 'firsttimer') {
      params.append('min_trips', '1');
      params.append('max_trips', '1');
    }

    window.open(`${BACKEND_URL}/api/directory/export?${params.toString()}`, '_blank');
    toast.success('CSV export started');
  };

  const clearFilters = () => {
    setSearch('');
    setRoleFilter('all');
    setYearFilter('all');
    setTripCountFilter('all');
    setContactedFilter('all');
    setContactedTripTag('');
  };

  const hasFilters = search || roleFilter !== 'all' || yearFilter !== 'all' || tripCountFilter !== 'all' || contactedFilter !== 'all';

  // Apply contacted filter client-side
  const filteredDirectory = contactedFilter !== 'all' && contactedTripTag
    ? directory.filter(entry => {
        const wasContacted = contactedEmails.has(entry.email);
        return contactedFilter === 'contacted' ? wasContacted : !wasContacted;
      })
    : directory;

  // Get selected emails and names for the modal
  const selectedEntries = directory.filter(e => selectedIds.has(e.user_id));
  const selectedEmails = selectedEntries.map(e => e.email).filter(Boolean);
  const selectedNames = selectedEntries.map(e => e.name);

  const dirActions = (
    <Button size="sm" variant="outline" className="rounded-full h-8 text-xs"
      onClick={handleExport} disabled={directory.length === 0} data-testid="export-csv-btn">
      <Download className="w-3.5 h-3.5 sm:mr-1.5" />
      <span className="hidden sm:inline">Export CSV</span>
    </Button>
  );

  return (
    <PageLayout title="Directory" headerActions={dirActions} maxWidth="max-w-5xl">
          {/* Stats bar */}
          <div className="flex flex-wrap gap-4 mb-5 text-sm">
            <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-xl border border-slate-200">
              <Users className="w-4 h-4 text-primary" />
              <span className="text-slate-600"><strong>{stats.total_participants}</strong> with trips</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-xl border border-slate-200">
              <Award className="w-4 h-4 text-amber-500" />
              <span className="text-slate-600"><strong>{stats.veterans}</strong> veterans (3+ trips)</span>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[200px] sm:flex-none sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search name, email, org..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 h-9"
                data-testid="directory-search"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="h-9 w-full sm:w-40" data-testid="filter-role">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="surgeon">Surgeons</SelectItem>
                <SelectItem value="anesthesia">Anesthesia</SelectItem>
                <SelectItem value="nurse">Nurses / Techs</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="h-9 w-full sm:w-32" data-testid="filter-year">
                <SelectValue placeholder="Any Year" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any Year</SelectItem>
                {stats.years_active.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={tripCountFilter} onValueChange={setTripCountFilter}>
              <SelectTrigger className="h-9 w-full sm:w-36" data-testid="filter-trips">
                <SelectValue placeholder="Trip Count" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any # of Trips</SelectItem>
                <SelectItem value="veteran">Veterans (3+)</SelectItem>
                <SelectItem value="firsttimer">First-timers</SelectItem>
                <SelectItem value="none">No trips yet</SelectItem>
              </SelectContent>
            </Select>
            {/* Contacted filter */}
            <Select value={contactedFilter} onValueChange={(v) => { setContactedFilter(v); if (v === 'all') setContactedTripTag(''); }}>
              <SelectTrigger className="h-9 w-full sm:w-44" data-testid="filter-contacted">
                <SelectValue placeholder="Email Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Email Status: Any</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="not_contacted">Not Contacted</SelectItem>
              </SelectContent>
            </Select>
            {contactedFilter !== 'all' && (
              <Select value={contactedTripTag || 'pick'} onValueChange={(v) => setContactedTripTag(v === 'pick' ? '' : v)}>
                <SelectTrigger className="h-9 w-full sm:w-48" data-testid="filter-contacted-trip">
                  <SelectValue placeholder="About which trip?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pick">Select trip...</SelectItem>
                  {[...new Set(trips.map(t => t.start_date?.substring(0, 4)).filter(Boolean))].sort().reverse().map(year => (
                    <SelectItem key={`y-${year}`} value={`${year} Trips`}>{year} Trips (general)</SelectItem>
                  ))}
                  {trips.filter(t => t.status !== 'completed').map(t => (
                    <SelectItem key={t.trip_id} value={t.name}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {hasFilters && (
              <Button variant="ghost" size="sm" className="h-9 text-slate-500" onClick={clearFilters}>
                <X className="w-3.5 h-3.5 mr-1" />Clear
              </Button>
            )}
          </div>

          {/* Directory list */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            {/* Select all header */}
            {directory.length > 0 && (
              <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 border-b border-slate-200">
                <div onClick={selectAll} className="cursor-pointer">
                  <Checkbox
                    checked={selectedIds.size === directory.length && directory.length > 0}
                    className="data-[state=checked]:bg-primary"
                    data-testid="select-all"
                  />
                </div>
                <span className="text-xs text-slate-500">
                  {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}
                </span>
                {selectedIds.size > 0 && (
                  <Button
                    size="sm"
                    className="ml-auto h-7 text-xs rounded-full"
                    onClick={() => setEmailModalOpen(true)}
                    data-testid="compose-email-btn"
                  >
                    <Mail className="w-3.5 h-3.5 mr-1.5" />
                    Email Selected
                  </Button>
                )}
              </div>
            )}

            {loading ? (
              <div className="text-center py-16">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : filteredDirectory.length === 0 ? (
              <div className="text-center py-16">
                <Users className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">No people found</p>
                {hasFilters && (
                  <Button variant="link" className="mt-2 text-primary" onClick={clearFilters}>
                    Clear filters
                  </Button>
                )}
              </div>
            ) : (
              filteredDirectory.map(entry => (
                <DirectoryRow
                  key={entry.user_id}
                  entry={entry}
                  expanded={expandedIds.has(entry.user_id)}
                  onToggle={() => toggleExpanded(entry.user_id)}
                  selected={selectedIds.has(entry.user_id)}
                  onSelect={toggleSelected}
                  onDelete={handleDeleteUser}
                />
              ))
            )}
          </div>

          {/* Footer hint */}
          {directory.length > 0 && (
            <p className="text-xs text-slate-400 mt-3 text-center">
              Click a row to expand trip history · Select people to send bulk email
            </p>
          )}

      {/* Bulk Email Modal */}
      <BulkEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        selectedEmails={selectedEmails}
        selectedNames={selectedNames}
      />
    </PageLayout>
  );
};

export default DirectoryPage;
